/**
 * Classification Logging System
 *
 * This module provides a unified interface for classification logging,
 * backed by the UniversalLogger system. It maintains backward compatibility
 * with existing code while consolidating logging into a single system.
 *
 * @see QuickLoggerFactory.classification() for the underlying implementation
 */

import { logAuditEvent, AuditLogCategory, AuditLogSeverity } from './audit-logger';
import { getClassificationConfig } from '@/lib/classification-config';
import { QuickLoggerFactory } from '@/lib/universal-logger';

// Use the classification namespace from UniversalLogger
const universalLogger = QuickLoggerFactory.classification();

/**
 * Classification log levels
 */
export enum ClassificationLogLevel {
  ERROR = 'error',
  WARN = 'warn', 
  INFO = 'info',
  DEBUG = 'debug'
}

/**
 * Classification event types
 */
export enum ClassificationEventType {
  // Lifecycle events
  CLASSIFICATION_STARTED = 'classification_started',
  CLASSIFICATION_COMPLETED = 'classification_completed',
  CLASSIFICATION_FAILED = 'classification_failed',
  
  // Service events
  SERVICE_INITIALIZED = 'service_initialized',
  SERVICE_HEALTH_CHECK = 'service_health_check',
  
  // API events
  API_REQUEST_STARTED = 'api_request_started',
  API_REQUEST_COMPLETED = 'api_request_completed',
  API_REQUEST_FAILED = 'api_request_failed',
  
  // Prompt processing events
  PROMPT_PREPROCESSING_STARTED = 'prompt_preprocessing_started',
  PROMPT_PREPROCESSING_COMPLETED = 'prompt_preprocessing_completed',
  PROMPT_SANITIZATION_APPLIED = 'prompt_sanitization_applied',
  
  // Category resolution events
  CATEGORY_LOOKUP_STARTED = 'category_lookup_started',
  CATEGORY_LOOKUP_COMPLETED = 'category_lookup_completed',
  CATEGORY_FALLBACK_APPLIED = 'category_fallback_applied',
  
  // Performance events
  PERFORMANCE_METRIC_RECORDED = 'performance_metric_recorded',
  TIMEOUT_OCCURRED = 'timeout_occurred',
  FALLBACK_TRIGGERED = 'fallback_triggered',
  
  // Storage events
  RESULT_STORAGE_STARTED = 'result_storage_started',
  RESULT_STORAGE_COMPLETED = 'result_storage_completed',
  RESULT_STORAGE_FAILED = 'result_storage_failed',
  
  // Configuration events
  CONFIG_LOADED = 'config_loaded',
  CONFIG_VALIDATION_FAILED = 'config_validation_failed'
}

/**
 * Classification log entry interface
 */
export interface ClassificationLogEntry {
  // Core identification
  timestamp: string;
  level: ClassificationLogLevel;
  eventType: ClassificationEventType;
  correlationId: string;
  
  // Context information
  promptId?: string;
  userId?: string;
  sessionId?: string;
  
  // Event-specific data
  message: string;
  details: Record<string, any>;
  
  // Performance metrics
  duration?: number;
  memoryUsage?: number;
  
  // Error information
  error?: {
    name: string;
    message: string;
    stack?: string;
    code?: string | number;
  };
  
  // Classification-specific context
  classificationContext?: {
    category?: string;
    confidence?: number;
    model?: string;
    strategy?: string;
    fallbackUsed?: boolean;
  };
}

/**
 * Correlation ID generator for tracking requests across services
 */
export class CorrelationIdGenerator {
  private static counter = 0;
  
  /**
   * Generate a unique correlation ID
   */
  static generate(): string {
    this.counter = (this.counter + 1) % 999999;
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substr(2, 5);
    return `cls_${timestamp}_${this.counter.toString().padStart(6, '0')}_${random}`;
  }
}

/**
 * Classification context manager for tracking request state
 */
export class ClassificationContext {
  private static contexts = new Map<string, ClassificationLogEntry>();
  
  /**
   * Create a new classification context
   */
  static create(promptId?: string, userId?: string): string {
    const correlationId = CorrelationIdGenerator.generate();
    const context: ClassificationLogEntry = {
      timestamp: new Date().toISOString(),
      level: ClassificationLogLevel.INFO,
      eventType: ClassificationEventType.CLASSIFICATION_STARTED,
      correlationId,
      promptId,
      userId,
      message: 'Classification context created',
      details: {}
    };
    
    this.contexts.set(correlationId, context);
    return correlationId;
  }
  
  /**
   * Get existing context by correlation ID
   */
  static get(correlationId: string): ClassificationLogEntry | undefined {
    return this.contexts.get(correlationId);
  }
  
  /**
   * Update context with new information
   */
  static update(correlationId: string, updates: Partial<ClassificationLogEntry>): void {
    const context = this.contexts.get(correlationId);
    if (context) {
      Object.assign(context, updates);
    }
  }
  
  /**
   * Clean up completed contexts to prevent memory leaks
   */
  static cleanup(correlationId: string): void {
    this.contexts.delete(correlationId);
  }
  
  /**
   * Get all active contexts (for debugging)
   */
  static getActiveContexts(): Map<string, ClassificationLogEntry> {
    return new Map(this.contexts);
  }
}

/**
 * Performance metrics collector
 */
export class PerformanceMetrics {
  private static metrics = new Map<string, number>();
  
  /**
   * Start timing an operation
   */
  static startTimer(operationId: string): void {
    this.metrics.set(`${operationId}_start`, performance.now());
  }
  
  /**
   * End timing an operation and return duration
   */
  static endTimer(operationId: string): number {
    const startTime = this.metrics.get(`${operationId}_start`);
    if (!startTime) {
      return 0;
    }
    
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    this.metrics.delete(`${operationId}_start`);
    this.metrics.set(`${operationId}_duration`, duration);
    
    return duration;
  }
  
  /**
   * Get current memory usage (if available)
   */
  static getMemoryUsage(): number | undefined {
    if (typeof window !== 'undefined' && 'memory' in performance) {
      return (performance as any).memory?.usedJSHeapSize;
    }
    return undefined;
  }
  
  /**
   * Record a custom metric
   */
  static recordMetric(name: string, value: number): void {
    this.metrics.set(name, value);
  }
  
  /**
   * Get all recorded metrics
   */
  static getAllMetrics(): Record<string, number> {
    return Object.fromEntries(this.metrics);
  }
}

/**
 * Main classification logger class
 */
export class ClassificationLogger {
  private static instance: ClassificationLogger | null = null;
  private config: ReturnType<typeof getClassificationConfig>;
  private logQueue: ClassificationLogEntry[] = [];
  private isProcessing = false;
  
  private constructor() {
    this.config = getClassificationConfig();
  }
  
  /**
   * Get environment-specific logging configuration
   */
  private getEnvironmentConfig(): {
    logLevel: ClassificationLogLevel;
    enableAuditLogging: boolean;
    enableConsoleOutput: boolean;
    enablePerformanceMetrics: boolean;
    logRetentionDays: number;
    maxLogEntries: number;
    enableSensitiveDataFiltering: boolean;
  } {
    const env = import.meta.env;
    
    // Determine environment
    const isDevelopment = env.NODE_ENV === 'development' || env.DEV;
    const isProduction = env.NODE_ENV === 'production' || env.PROD;
    const isTest = env.NODE_ENV === 'test';
    
    // Environment-specific defaults
    let defaults = {
      logLevel: ClassificationLogLevel.INFO,
      enableAuditLogging: true,
      enableConsoleOutput: true,
      enablePerformanceMetrics: true,
      logRetentionDays: 30,
      maxLogEntries: 10000,
      enableSensitiveDataFiltering: true
    };
    
    if (isDevelopment) {
      defaults = {
        ...defaults,
        logLevel: ClassificationLogLevel.DEBUG,
        enableConsoleOutput: true,
        enablePerformanceMetrics: true,
        logRetentionDays: 7,
        enableSensitiveDataFiltering: false // Allow full logging in dev
      };
    } else if (isProduction) {
      defaults = {
        ...defaults,
        logLevel: ClassificationLogLevel.WARN,
        enableConsoleOutput: false, // Disable console in production
        enablePerformanceMetrics: true,
        logRetentionDays: 90,
        enableSensitiveDataFiltering: true // Always filter in production
      };
    } else if (isTest) {
      defaults = {
        ...defaults,
        logLevel: ClassificationLogLevel.ERROR,
        enableAuditLogging: false,
        enableConsoleOutput: false,
        enablePerformanceMetrics: false,
        logRetentionDays: 1,
        maxLogEntries: 100,
        enableSensitiveDataFiltering: false
      };
    }
    
    // Override with environment variables
    return {
      logLevel: (env.VITE_CLASSIFICATION_LOG_LEVEL as ClassificationLogLevel) || defaults.logLevel,
      enableAuditLogging: env.VITE_CLASSIFICATION_ENABLE_AUDIT_LOGGING === 'true' ? true : 
                         env.VITE_CLASSIFICATION_ENABLE_AUDIT_LOGGING === 'false' ? false : defaults.enableAuditLogging,
      enableConsoleOutput: env.VITE_CLASSIFICATION_ENABLE_CONSOLE_OUTPUT === 'true' ? true :
                          env.VITE_CLASSIFICATION_ENABLE_CONSOLE_OUTPUT === 'false' ? false : defaults.enableConsoleOutput,
      enablePerformanceMetrics: env.VITE_CLASSIFICATION_ENABLE_PERFORMANCE_METRICS === 'true' ? true :
                               env.VITE_CLASSIFICATION_ENABLE_PERFORMANCE_METRICS === 'false' ? false : defaults.enablePerformanceMetrics,
      logRetentionDays: parseInt(env.VITE_CLASSIFICATION_LOG_RETENTION_DAYS as string) || defaults.logRetentionDays,
      maxLogEntries: parseInt(env.VITE_CLASSIFICATION_MAX_LOG_ENTRIES as string) || defaults.maxLogEntries,
      enableSensitiveDataFiltering: env.VITE_CLASSIFICATION_ENABLE_SENSITIVE_DATA_FILTERING === 'true' ? true :
                                   env.VITE_CLASSIFICATION_ENABLE_SENSITIVE_DATA_FILTERING === 'false' ? false : defaults.enableSensitiveDataFiltering
    };
  }
  
  /**
   * Get singleton instance
   */
  static getInstance(): ClassificationLogger {
    if (!this.instance) {
      this.instance = new ClassificationLogger();
    }
    return this.instance;
  }
  
  /**
   * Check if logging is enabled for a given level
   */
  private isLogLevelEnabled(level: ClassificationLogLevel): boolean {
    const levels = [
      ClassificationLogLevel.ERROR,
      ClassificationLogLevel.WARN,
      ClassificationLogLevel.INFO,
      ClassificationLogLevel.DEBUG
    ];
    
    const currentLevelIndex = levels.indexOf(this.getLogLevel());
    const requestedLevelIndex = levels.indexOf(level);
    
    return requestedLevelIndex <= currentLevelIndex;
  }
  
  /**
   * Get current log level from environment configuration
   */
  private getLogLevel(): ClassificationLogLevel {
    const envConfig = this.getEnvironmentConfig();
    
    // If logging is disabled in main config, only allow ERROR level
    if (!this.config.enableLogging) {
      return ClassificationLogLevel.ERROR;
    }
    
    return envConfig.logLevel;
  }
  
  /**
   * Create a structured log entry
   */
  private createLogEntry(
    level: ClassificationLogLevel,
    eventType: ClassificationEventType,
    message: string,
    correlationId: string,
    details: Record<string, any> = {},
    error?: Error,
    duration?: number
  ): ClassificationLogEntry {
    const entry: ClassificationLogEntry = {
      timestamp: new Date().toISOString(),
      level,
      eventType,
      correlationId,
      message,
      details: this.sanitizeDetails(details),
      duration,
      memoryUsage: PerformanceMetrics.getMemoryUsage()
    };
    
    // Add error information if provided
    if (error) {
      entry.error = {
        name: error.name,
        message: error.message,
        stack: error.stack,
        code: (error as any).code
      };
    }
    
    // Add context information if available
    const context = ClassificationContext.get(correlationId);
    if (context) {
      entry.promptId = context.promptId;
      entry.userId = context.userId;
      entry.sessionId = context.sessionId;
      entry.classificationContext = context.classificationContext;
    }
    
    return entry;
  }
  
  /**
   * Sanitize sensitive data from log details
   */
  private sanitizeDetails(details: Record<string, any>): Record<string, any> {
    const envConfig = this.getEnvironmentConfig();
    
    // If sensitive data filtering is disabled, return as-is
    if (!envConfig.enableSensitiveDataFiltering) {
      return details;
    }
    
    const sensitiveKeys = [
      'apiKey', 'api_key', 'token', 'password', 'secret', 'auth', 'authorization',
      'key', 'credential', 'bearer', 'jwt', 'session', 'cookie'
    ];
    
    const sanitized: Record<string, any> = {};
    
    Object.entries(details).forEach(([key, value]) => {
      const isSensitive = sensitiveKeys.some(sensitive => 
        key.toLowerCase().includes(sensitive.toLowerCase())
      );
      
      if (isSensitive && typeof value === 'string') {
        sanitized[key] = `***REDACTED(${value.length})***`;
      } else if (value && typeof value === 'object' && !Array.isArray(value)) {
        sanitized[key] = this.sanitizeDetails(value);
      } else {
        sanitized[key] = value;
      }
    });
    
    return sanitized;
  }
  
  /**
   * Output log entry to appropriate destination
   * Delegates to UniversalLogger for consistent logging across the application
   */
  private outputLog(entry: ClassificationLogEntry): void {
    const envConfig = this.getEnvironmentConfig();

    // Prepare log context for UniversalLogger
    const logContext = {
      correlationId: entry.correlationId,
      eventType: entry.eventType,
      promptId: entry.promptId,
      userId: entry.userId,
      sessionId: entry.sessionId,
      duration: entry.duration,
      memoryUsage: entry.memoryUsage,
      classificationContext: entry.classificationContext,
      ...entry.details
    };

    // Delegate to UniversalLogger which handles console output based on environment
    switch (entry.level) {
      case ClassificationLogLevel.ERROR:
        universalLogger.error(entry.message, logContext, entry.error ? new Error(entry.error.message) : undefined);
        break;
      case ClassificationLogLevel.WARN:
        universalLogger.warn(entry.message, logContext);
        break;
      case ClassificationLogLevel.INFO:
        universalLogger.info(entry.message, logContext);
        break;
      case ClassificationLogLevel.DEBUG:
        universalLogger.debug(entry.message, logContext);
        break;
    }

    // Send to audit logger if enabled and for important events
    if (envConfig.enableAuditLogging && (
        entry.level === ClassificationLogLevel.ERROR ||
        entry.eventType === ClassificationEventType.CLASSIFICATION_COMPLETED)) {
      this.sendToAuditLog(entry);
    }
  }
  
  /**
   * Send entry to audit logging system
   */
  private async sendToAuditLog(entry: ClassificationLogEntry): Promise<void> {
    try {
      const severity = entry.level === ClassificationLogLevel.ERROR 
        ? AuditLogSeverity.ERROR 
        : AuditLogSeverity.INFO;
        
      await logAuditEvent(
        `classification_${entry.eventType}`,
        AuditLogCategory.SYSTEM,
        severity,
        {
          correlationId: entry.correlationId,
          promptId: entry.promptId,
          eventType: entry.eventType,
          duration: entry.duration,
          details: entry.details,
          error: entry.error
        },
        entry.userId
      );
    } catch (error) {
      // Log through Universal Logger (which handles console output based on environment)
      universalLogger.error('Failed to send classification log to audit system', { error, correlationId: entry.correlationId });
    }
  }
  
  /**
   * Process the log queue
   */
  private async processLogQueue(): Promise<void> {
    if (this.isProcessing || this.logQueue.length === 0) {
      return;
    }
    
    this.isProcessing = true;
    const envConfig = this.getEnvironmentConfig();
    
    try {
      // Perform cleanup before processing if needed
      this.performLogCleanup();
      
      // Process logs in batches to avoid blocking
      const batchSize = envConfig.enablePerformanceMetrics ? 5 : 10; // Smaller batches with metrics
      while (this.logQueue.length > 0) {
        const batch = this.logQueue.splice(0, batchSize);
        
        // Record performance metrics if enabled
        if (envConfig.enablePerformanceMetrics) {
          const startTime = performance.now();
          
          for (const entry of batch) {
            this.outputLog(entry);
          }
          
          const processingTime = performance.now() - startTime;
          PerformanceMetrics.recordMetric('log_batch_processing_time', processingTime);
          PerformanceMetrics.recordMetric('log_batch_size', batch.length);
        } else {
          for (const entry of batch) {
            this.outputLog(entry);
          }
        }
        
        // Small delay to prevent blocking
        await new Promise(resolve => setTimeout(resolve, 1));
      }
    } finally {
      this.isProcessing = false;
    }
  }
  
  /**
   * Add log entry to queue
   */
  private queueLog(entry: ClassificationLogEntry): void {
    this.logQueue.push(entry);
    
    // Process queue asynchronously
    setTimeout(() => this.processLogQueue(), 0);
  }
  
  /**
   * Log a classification event
   */
  log(
    level: ClassificationLogLevel,
    eventType: ClassificationEventType,
    message: string,
    correlationId: string,
    details: Record<string, any> = {},
    error?: Error,
    duration?: number
  ): void {
    if (!this.isLogLevelEnabled(level)) {
      return;
    }
    
    const entry = this.createLogEntry(
      level,
      eventType,
      message,
      correlationId,
      details,
      error,
      duration
    );
    
    this.queueLog(entry);
  }
  
  /**
   * Convenience methods for different log levels
   */
  error(eventType: ClassificationEventType, message: string, correlationId: string, details?: Record<string, any>, error?: Error): void {
    this.log(ClassificationLogLevel.ERROR, eventType, message, correlationId, details, error);
  }
  
  warn(eventType: ClassificationEventType, message: string, correlationId: string, details?: Record<string, any>): void {
    this.log(ClassificationLogLevel.WARN, eventType, message, correlationId, details);
  }
  
  info(eventType: ClassificationEventType, message: string, correlationId: string, details?: Record<string, any>): void {
    this.log(ClassificationLogLevel.INFO, eventType, message, correlationId, details);
  }
  
  debug(eventType: ClassificationEventType, message: string, correlationId: string, details?: Record<string, any>): void {
    this.log(ClassificationLogLevel.DEBUG, eventType, message, correlationId, details);
  }
  
  /**
   * Get logging statistics
   */
  getStats(): Record<string, any> {
    const envConfig = this.getEnvironmentConfig();
    
    return {
      queueLength: this.logQueue.length,
      isProcessing: this.isProcessing,
      logLevel: this.getLogLevel(),
      loggingEnabled: this.config.enableLogging,
      activeContexts: ClassificationContext.getActiveContexts().size,
      performanceMetrics: PerformanceMetrics.getAllMetrics(),
      environmentConfig: {
        enableConsoleOutput: envConfig.enableConsoleOutput,
        enableAuditLogging: envConfig.enableAuditLogging,
        enablePerformanceMetrics: envConfig.enablePerformanceMetrics,
        enableSensitiveDataFiltering: envConfig.enableSensitiveDataFiltering,
        logRetentionDays: envConfig.logRetentionDays,
        maxLogEntries: envConfig.maxLogEntries
      }
    };
  }
  
  /**
   * Perform environment-aware log cleanup
   */
  performLogCleanup(): void {
    const envConfig = this.getEnvironmentConfig();
    
    // Limit queue size based on environment configuration
    if (this.logQueue.length > envConfig.maxLogEntries) {
      const excessLogs = this.logQueue.length - envConfig.maxLogEntries;
      this.logQueue.splice(0, excessLogs);
      
      this.log(
        ClassificationLogLevel.INFO,
        ClassificationEventType.CONFIG_LOADED,
        `Log queue trimmed: removed ${excessLogs} old entries`,
        'log_cleanup',
        { 
          queueSize: this.logQueue.length, 
          maxEntries: envConfig.maxLogEntries,
          environment: import.meta.env.NODE_ENV 
        }
      );
    }
  }
  
  /**
   * Get environment-specific configuration summary
   */
  getEnvironmentConfigSummary(): Record<string, any> {
    const envConfig = this.getEnvironmentConfig();
    const env = import.meta.env;
    
    return {
      environment: env.NODE_ENV || 'development',
      isDevelopment: env.NODE_ENV === 'development' || env.DEV,
      isProduction: env.NODE_ENV === 'production' || env.PROD,
      isTest: env.NODE_ENV === 'test',
      configuration: envConfig,
      overrides: {
        logLevel: env.VITE_CLASSIFICATION_LOG_LEVEL || 'default',
        consoleOutput: env.VITE_CLASSIFICATION_ENABLE_CONSOLE_OUTPUT || 'default',
        auditLogging: env.VITE_CLASSIFICATION_ENABLE_AUDIT_LOGGING || 'default',
        performanceMetrics: env.VITE_CLASSIFICATION_ENABLE_PERFORMANCE_METRICS || 'default',
        sensitiveDataFiltering: env.VITE_CLASSIFICATION_ENABLE_SENSITIVE_DATA_FILTERING || 'default',
        retentionDays: env.VITE_CLASSIFICATION_LOG_RETENTION_DAYS || 'default',
        maxEntries: env.VITE_CLASSIFICATION_MAX_LOG_ENTRIES || 'default'
      }
    };
  }
}

/**
 * Convenience function to get logger instance
 */
export const getClassificationLogger = (): ClassificationLogger => 
  ClassificationLogger.getInstance();

/**
 * Higher-order function for automatically logging function execution
 */
export function withClassificationLogging<T extends (...args: any[]) => any>(
  fn: T,
  eventType: ClassificationEventType,
  correlationId: string
): T {
  return ((...args: any[]) => {
    const logger = getClassificationLogger();
    const operationId = `${eventType}_${correlationId}`;
    
    PerformanceMetrics.startTimer(operationId);
    logger.debug(eventType, `Starting ${fn.name || 'anonymous function'}`, correlationId);
    
    try {
      const result = fn(...args);
      
      if (result instanceof Promise) {
        return result
          .then((resolved) => {
            const duration = PerformanceMetrics.endTimer(operationId);
            logger.debug(eventType, `Completed ${fn.name || 'anonymous function'}`, correlationId, { duration });
            return resolved;
          })
          .catch((error) => {
            const duration = PerformanceMetrics.endTimer(operationId);
            logger.error(eventType, `Failed ${fn.name || 'anonymous function'}`, correlationId, { duration }, error);
            throw error;
          });
      } else {
        const duration = PerformanceMetrics.endTimer(operationId);
        logger.debug(eventType, `Completed ${fn.name || 'anonymous function'}`, correlationId, { duration });
        return result;
      }
    } catch (error) {
      const duration = PerformanceMetrics.endTimer(operationId);
      logger.error(eventType, `Failed ${fn.name || 'anonymous function'}`, correlationId, { duration }, error as Error);
      throw error;
    }
  }) as T;
} 